import { ReactNode } from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';

import { FontSizes, FontWeights, Text } from '@calm-web/design-system';

import LimitedTimeCallout from '@/components/PreSignupFlow/Components/LimitedTimeCallout';
import paymentMessages from '@/components/SimplifiedSignup/Payment/messages';
import { isOfferValid } from '@/components/SimplifiedSignup/utils/partnership';
import { useAuthPrompt } from '@/context/auth/AuthPromptProvider';
import { useFormattedCurrency } from '@/hooks/purchase';
import {
	useCampaignConfigState,
	usePartnerState,
	usePricesState,
	usePurchaseParamsState,
	useUserState,
} from '@/hooks/store';
import { useIsMobile } from '@/hooks/ui/useIsMobile';
import useQueryParams from '@/hooks/utils/useQueryParams';
import { isValidPlan } from '@/store/purchaseParams/types';

import messages from '../../Account/messages';
import { Card } from '../../Account/styles';
import { AccountScreenConfig } from '../../Account/types';
import { useSimplifiedSignupContext } from '../../Context';
import AuthIneligible from '../AuthIneligible';
import AuthForm from '../ConfigurableAuthForm';
import OfferHasEnded from '../OfferHasEnded';

const SisuAuthForm: React.FC = () => {
	const { name, activeScreenConfig } = useSimplifiedSignupContext();
	const {
		authHeaderSize,
		heading,
		limitedTimeOffer,
		terms,
		termsPrefix,
		termsSuffix,
		loginTerms,
		loginToggleText,
		signupTerms,
		signupToggleText,
		hideFacebookSignup = false,
		hideSocialSignup = false,
		hideAuthModeToggle = false,
		emailSignupCTA,
		subheading,
		subheadingPosition,
		isWithinModal,
		modeTogglePosition,
		showMarketingOptIn = true,
		ignoreSubscriptionStatus,
	} = activeScreenConfig as AccountScreenConfig;

	const [isMobile] = useIsMobile();
	const { formatMessage, formatDate } = useIntl();
	const formatCurrency = useFormattedCurrency();
	const prices = usePricesState();
	const purchaseParams = usePurchaseParamsState();
	const { authPrompt } = useAuthPrompt();
	const partner = usePartnerState();
	const campaignConfig = useCampaignConfigState();
	const user = useUserState();
	const query = useQueryParams();

	const buildHeading = () => {
		if ((isMobile && isWithinModal) || !heading) {
			return null;
		}

		const isSimplifiedFTUE: boolean = query?.utm_content?.includes('_ft-sf') || false;

		if (isSimplifiedFTUE && authPrompt === 'signup') {
			return formatMessage(messages.signupFormSimpleHeading);
		}

		const { message, placeholders = {} } = heading({
			authFormMode: authPrompt,
			prices,
			purchaseParams,
			formatCurrency,
		});

		return typeof message === 'string' ? message : formatMessage(message, placeholders);
	};

	const formatTerms = (terms: MessageDescriptor | undefined) => {
		if (!terms) return '';
		// Partners source their end date from contract_expires_at; campaigns have no such
		// contract and instead run until scheduled_unpublish_at, or indefinitely if unset — in
		// which case hasExpiration lets the message itself drop the "Offer valid through"
		// sentence, rather than rendering it with a blank date.
		const expiresAt = partner?.contract_expires_at ?? campaignConfig?.scheduled_unpublish_at;
		return formatMessage(terms, {
			hasExpiration: Boolean(expiresAt),
			date: expiresAt ? formatDate(new Date(expiresAt), { dateStyle: 'medium' }) : '',
			originalPrice: formatCurrency(
				prices.original[
					partner?.offer_details?.d2c_subscription_plan ||
						(isValidPlan(purchaseParams.plan) ? purchaseParams.plan : undefined) ||
						'yearly'
				],
			),
			cancel: (...chunks: ReactNode[]) => (
				<a
					href="https://support.calm.com/hc/en-us/articles/115002473607-How-to-turn-off-auto-renewal-or-cancel-my-subscription"
					target="_blank"
					rel="noreferrer"
				>
					{chunks}
				</a>
			),
			a: (...chunks: ReactNode[]) => (
				<a href="/terms" target="_blank">
					{chunks}
				</a>
			),
			privacypolicylink: (...chunks: ReactNode[]) => (
				<a href="/privacy" target="_blank">
					{chunks}
				</a>
			),
		});
	};

	const formatSubheading = () => {
		if (!subheading) return '';

		if (typeof subheading === 'function') {
			const { message, placeholders = {} } = subheading({
				authFormMode: authPrompt,
				prices,
				purchaseParams,
				formatCurrency,
			});
			return formatMessage(message, placeholders);
		}

		return formatMessage(subheading);
	};

	// Campaigns have no `partner` in state, so this used to only gate legacy partner flows —
	// an already-subscribed campaign visitor fell through to Payment and failed there instead
	// of seeing this earlier, friendlier message.
	const isIneligibleUser =
		user?.subscription?.valid && (partner || campaignConfig) && !ignoreSubscriptionStatus;
	if (isIneligibleUser) {
		return <AuthIneligible />;
	}

	const headingText = buildHeading();

	return (
		<div>
			{limitedTimeOffer && (
				<LimitedTimeCallout>{formatMessage(paymentMessages.limitedTimeCallout)}</LimitedTimeCallout>
			)}
			{headingText && (
				<Text
					align="center"
					noMargin
					el="h1"
					weight={FontWeights.Medium}
					size={authHeaderSize || FontSizes['2xl']}
					data-testid="sisu-auth-form-heading"
				>
					{headingText}
				</Text>
			)}
			<AuthForm
				loginTermsText={loginTerms && formatTerms(loginTerms)}
				loginToggleText={loginToggleText}
				signupTermsText={signupTerms && formatTerms(signupTerms)}
				signupToggleText={signupToggleText}
				partnerTerms={terms as string}
				termsPrefix={termsPrefix}
				termsSuffix={termsSuffix}
				showMarketingOptIn={showMarketingOptIn}
				customEventProps={{ source: name }}
				hideFacebookSignup={hideFacebookSignup || hideSocialSignup}
				hideGoogleSignup={hideSocialSignup}
				hideAppleSignup={hideSocialSignup}
				hideAuthModeToggle={
					typeof hideAuthModeToggle === 'function'
						? hideAuthModeToggle({ purchaseParams })
						: hideAuthModeToggle
				}
				emailSignupCTA={emailSignupCTA}
				subheading={formatSubheading()}
				subheadingPosition={subheadingPosition}
				modeTogglePosition={modeTogglePosition}
			/>
		</div>
	);
};

const SisiAuthFormWithWrapper = () => {
	const { activeScreenConfig } = useSimplifiedSignupContext();
	const [isMobile] = useIsMobile();
	const { isWithinModal } = activeScreenConfig as AccountScreenConfig;
	const partner = usePartnerState();

	if (isMobile && isWithinModal) {
		return <SisuAuthForm />;
	}

	if (!isOfferValid(partner)) {
		return <OfferHasEnded />;
	}
	return (
		<Card isWithinModal={isWithinModal}>
			<SisuAuthForm />
		</Card>
	);
};

export default SisiAuthFormWithWrapper;
